import { APP_STAGE, type Extend, isEmpty } from '@nfctron/nexus-core';

import useTranslation from 'next-translate/useTranslation';

import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';

/**
 * Default translation query key-value pairs
 * @export
 */
export const defaultTranslationQuery = {
	appStage: APP_STAGE,
} as const;

const getTransKey = (key: string, scope?: string, context?: string) => {
	let transKey = key;
	if (!isEmpty(scope)) transKey = `${scope}.${transKey}`;
	if (!isEmpty(context)) transKey = `${transKey}_${context}`;
	return transKey;
};

/**
 * Custom useTranslation hook with scope key path
 */
export function useTranslate<TNamespace extends APP_I18N_NAMESPACE = APP_I18N_NAMESPACE.MAIN>(
	ns: TNamespace = APP_I18N_NAMESPACE.MAIN as TNamespace,
	inputScope?: string | string[],
) {
	const scope = Array.isArray(inputScope) ? inputScope.join('.') : inputScope;
	/** useTranslation hook */
	const translation = useTranslation(ns);

	const t = <TRes extends unknown = string>(key: string, query?: TranslationQuery | null, options?: TFunctionOptions<TRes>) => {
		query = { ...defaultTranslationQuery, ...query };
		const res = translation.t<TRes>(getTransKey(key, scope, query.context), query, { ...options, fallback: '' });
		if (isEmpty(res)) {
			return translation.t<TRes>(getTransKey(key, scope, undefined), query, options);
		}
		return res;
	};

	/** translate function with null fallback */
	const tor = <TRes extends unknown = string, TFallback extends any | undefined | null = null>(
		key: string,
		query?: TranslationQuery | null,
		options?: TOrFunctionOptions<TRes, TFallback>,
	) => {
		query = { ...defaultTranslationQuery, ...query };
		const res = translation.t<TRes>(getTransKey(key, scope, undefined), query, { ...options, fallback: '' });
		if (isEmpty(res)) return options?.fallback as TFallback;
		return res;
	};

	return {
		lang: translation.lang,
		t,
		tor,
	};
}

/**
 * TFunction and its variations types
 * @export
 */
export type TFunction<TRes extends unknown = string> = (key: string, query?: TranslationQuery | null, options?: TFunctionOptions<TRes>) => TRes;
export type TFunctionOptions<TRes extends unknown = string> = {
	returnObjects?: boolean;
	fallback?: string | string[];
	default?: TRes | string;
	ns?: string;
};
export type TOrFunction<TRes extends unknown = string, TFallback extends unknown | null = null> = (
	key: string,
	query?: TranslationQuery | null,
	options?: TOrFunctionOptions<TRes, TFallback>,
) => string;
export type TOrFunctionOptions<TRes extends unknown = string, TFallback extends unknown | null = null> = Extend<
	TFunctionOptions<TRes>,
	{
		fallback?: TFallback;
	}
>;

export interface TranslationQuery {
	context?: string;
	count?: number;

	/** any other */
	[name: string]: any;
}
